import { Component, OnInit, OnDestroy, Inject, Renderer2, ViewChild, ElementRef, AfterViewInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ClientsTestimonalComponent } from '../../general/clients-testimonal/clients-testimonal.component';
import { GalleryComponent } from '../../general/gallery/gallery.component';
import { SuiteTechnologiesComponent } from '../../general/suite-technologies/suite-technologies.component';
import { RecentUpdatesComponent } from '../../general/recent-updates/recent-updates.component';
import { FaqComponent } from '../faq/faq.component';
import { FaqItem } from '../../models/faq.model';
import { trigger, style, animate, transition, state, query, stagger } from '@angular/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { ContactModalService } from '../../services/contact-modal.service';

export interface Industry {
  imageSrc: string;
  iconSrc: string;
  title: string;
  description: string;
  route: string;
}
type TemplateItem = Industry | { placeholder: true };

export interface Service {
  imageSrc: string;
  iconSrc: string;
  title: string;
  description: string;
  route: string;
}
type ServiceTemplateItem = Service | { placeholder: true };

@Component({
  selector: 'app-home',
  imports: [CommonModule, ClientsTestimonalComponent, GalleryComponent, SuiteTechnologiesComponent, RecentUpdatesComponent, FaqComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  standalone: true,
  animations: [
    // ---------------- INDUSTRIES ANIMATIONS ----------------
    trigger('industryImageAnim', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateX({{startOffset}}) scale(0.9)',
          zIndex: '{{zIndex}}'
        }),
        animate('1500ms {{delay}}ms cubic-bezier(0.2, 1, 0.3, 1)',
          style({ opacity: 1, transform: 'translateX(0) scale(1)' })
        )
      ], { params: { startOffset: '0%', delay: 0, zIndex: 1 } })
    ]),

    trigger('industryTextAnim', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY({{startOffset}})',
          zIndex: '{{zIndex}}'
        }),
        animate('1500ms {{delay}}ms cubic-bezier(0.2, 1, 0.3, 1)',
          style({ opacity: 1, transform: 'translateY(0)' })
        )
      ], { params: { startOffset: '0%', delay: 0, zIndex: 1 } })
    ]),

    // ---------------- SERVICES ANIMATIONS ----------------
    trigger('serviceImageAnim', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateX({{startOffset}}) scale(0.9)',
          zIndex: '{{zIndex}}'
        }),
        animate('1500ms {{delay}}ms cubic-bezier(0.2, 1, 0.3, 1)',
          style({ opacity: 1, transform: 'translateX(0) scale(1)' })
        )
      ], { params: { startOffset: '0%', delay: 0, zIndex: 1 } })
    ]),

    trigger('serviceTextAnim', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY({{startOffset}})',
          zIndex: '{{zIndex}}'
        }),
        animate('1500ms {{delay}}ms cubic-bezier(0.2, 1, 0.3, 1)',
          style({ opacity: 1, transform: 'translateY(0)' })
        )
      ], { params: { startOffset: '0%', delay: 0, zIndex: 1 } })
    ]),

    trigger('metricsAnim', [
      transition(':enter', [
        query('.metric-item', [
          style({ opacity: 0, transform: 'translateY(50px)' })
        ], { optional: true }),

        query('.metric-item', [
          stagger('300ms', [
            animate('1500ms cubic-bezier(0.35, 0, 0.25, 1)',
              style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ]),
      transition(':leave', [
        query('.metric-item', [
          stagger('100ms', [
            animate('500ms ease-out',
              style({ opacity: 0, transform: 'translateY(50px)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})

export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('milestoneVideo') milestoneVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('heroVideo', { static: true }) heroVideo!: ElementRef<HTMLVideoElement>;

  private lazyLoadObserver: IntersectionObserver | null = null;
  private animationObserver: IntersectionObserver | null = null;
  private videoObserver: IntersectionObserver | null = null;
  private navigationObserver: IntersectionObserver | null = null;
  private servicesInitialized = false;

  loadedSections: Set<string> = new Set(['hero', 'clients']);

  sections = [
    { id: 'hero', label: 'Hero Banner' },
    { id: 'clients', label: 'Clients' },
    { id: 'testimonials', label: 'Testimonials' },
    { id: 'milestones', label: 'Milestones' },
    { id: 'industries', label: 'Industries' },
    { id: 'technology', label: 'Technology' },
    { id: 'about', label: 'About Us' },
    { id: 'services', label: 'Services' },
    { id: 'recentupdates', label: 'News/Blog/WhitePaper' },
    { id: 'gallery', label: 'Gallery' }
  ];

  allIndustries: Industry[] = [
    {
      imageSrc: 'assets/home/industries/healthcare_image.png',
      iconSrc: 'assets/home/industries/healthcare_icon.webp',
      title: 'HealthTech',
      description: 'Compliant healthcare systems that simplify patient care and clinical operations — including the ARC engine that centralised 18 internal and 14 third-party applications on AWS.',
      route: 'industries/healthtech'
    },
    {
      imageSrc: 'assets/home/industries/media_img.png',
      iconSrc: 'assets/home/industries/media_icon.webp',
      title: 'Media & Entertainment',
      description: 'OTT, streaming, and content platforms engineered for speed, scale, and audience engagement.',
      route: 'industries/media-entertainment'
    },
    {
      imageSrc: 'assets/home/industries/ecommerce_img.png',
      iconSrc: 'assets/home/industries/ecommerce_icon.webp',
      title: 'E-commerce',
      description: 'High-performance commerce platforms with smart automation and secure integrations — from end-to-end SaaS storefronts to multi-region retail mobile apps.',
      route: 'industries/ecommerce'
    },
    {
      imageSrc: 'assets/home/industries/insurance_img.png',
      iconSrc: 'assets/home/industries/insurance_icon.webp',
      title: 'InsurTech',
      description: 'AI-driven insurance platforms that automate policy workflows, accelerate claims, and lift policyholder experience. For Smart Insure, onboarding efficiency rose 30%.',
      route: 'industries/insurtech'
    },
    {
      imageSrc: 'assets/home/industries/finance_img.png',
      iconSrc: 'assets/home/industries/finance_icon.webp',
      title: 'FinTech',
      description: 'Secure financial software that improves data accuracy, automates reporting, and sharpens decision-making — proven on enterprise data warehouse and Power BI reporting engagements.',
      route: 'industries/fintech'
    },
    {
      imageSrc: 'assets/home/industries/elearning_img.png',
      iconSrc: 'assets/home/industries/elearning_icon.webp',
      title: 'EdTech',
      description: 'Interactive learning platforms with personalised learning paths, analytics, and digital classroom experiences.',
      route: 'industries/edtech'
    },
    {
      imageSrc: 'assets/home/industries/hi-tech-img.png',
      iconSrc: 'assets/home/industries/hi-tech-icon.webp',
      title: 'Hi-Tech ',
      description: 'AI, automation, and cloud engineering for product companies that need to ship faster than their category.',
      route: 'industries/hitech'
    }
  ];

  allServices: Service[] = [
    {
      imageSrc: 'assets/home/services/softwaredev.png',
      iconSrc: 'assets/home/services/software-dev.webp',
      title: 'Custom Software Development',
      description: 'Purpose-built software for the workflows off-the-shelf tools can\'t handle — engineered for scale, efficiency, and long-term growth.',
      route: 'company-services/custom-software-development'
    },
    {
      imageSrc: 'assets/home/services/webdev.png',
      iconSrc: 'assets/home/services/web-dev.webp',
      title: 'Web Development',
      description: 'Responsive, secure, SEO- and AEO-optimised websites and web apps that turn your online presence into a growth channel.',
      route: 'company-services/web-development'
    },
    {
      imageSrc: 'assets/home/services/mbldev.png',
      iconSrc: 'assets/home/services/mbl-dev.webp',
      title: 'Mobile App Development',
      description: 'High-performance iOS and Android apps — native and cross-platform — that users keep and businesses measure.',
      route: 'company-services/mobile-app-development'
    },
    {
      imageSrc: 'assets/home/services/ui-ux-img.png',
      iconSrc: 'assets/home/services/ui-ux.webp',
      title: 'UI/UX Design',
      description: 'Interfaces designed around the person using them: clear, intuitive, conversion-aware.',
      route: 'company-services/ui-ux-design'
    },
    {
      imageSrc: 'assets/home/services/data-analytics-img.png',
      iconSrc: 'assets/home/services/data-analytics.webp',
      title: 'Data Engineering',
      description: 'Pipelines, warehouses, and BI dashboards that make your data accurate, accessible, and decision-ready.',
      route: 'company-services/data-engineering'
    },
    {
      imageSrc: 'assets/home/services/qa-img.png',
      iconSrc: 'assets/home/services/qa.webp',
      title: 'Testing & QA',
      description: 'End-to-end quality engineering that reduces QA effort by 40–50% while protecting every release.',
      route: 'company-services/testing-qa'
    },
    {
      imageSrc: 'assets/home/services/it-consulting.png',
      iconSrc: 'assets/home/services/it-consultant.webp',
      title: 'IT Consulting',
      description: 'Technology strategy, modernisation roadmaps, and architecture guidance from engineers who build what they recommend.',
      route: 'company-services/it-consulting'
    },
    {
      imageSrc: 'assets/home/services/ai-as-a-service.png',
      iconSrc: 'assets/home/services/AI-as-service.webp',
      title: 'AI as a Service',
      description: 'RAG pipelines, agentic workflows, NLP, and computer vision — production AI, deployed where your data lives.',
      route: 'company-services/ai-as-a-service'
    },
    {
      imageSrc: 'assets/home/services/digital-marketing-img.png',
      iconSrc: 'assets/home/services/digital-marketing.webp',
      title: 'Digital Marketing',
      description: 'SEO, AEO, and performance marketing that make your brand visible in both classic search and AI answers.',
      route: 'company-services/digital-marketing'
    }
  ];

  faqList: FaqItem[] = [
    {
      question: 'What is an AI-first software development company?',
      answer: 'An AI-first software development company embeds AI into every phase of the software lifecycle — requirements, coding, testing, deployment, and operations — instead of treating AI as an add-on feature. Kalpita Technologies works this way by default: 70%+ of our engineering is AI-enabled, using a Multi-LLM Strategy, RAG pipelines, and AI-assisted development tooling.'
    },
    {
      question: 'What is the AI-first approach in software development?',
      answer: 'The AI-first approach means designing systems with intelligence built in, not bolted on. In practice: AI-assisted coding and testing, retrieval-augmented architectures that ground outputs in your data, and agentic workflows that automate multi-step processes. The result is 30–50% faster delivery and software that improves with use.'
    },
    {
      question: 'How do AI-first companies build software differently from traditional firms?',
      answer: 'Traditional firms write, test, and document software manually, then consider AI features later. AI-first companies like Kalpita use AI at every step — code generation with human review, automated test design, AI-driven security scanning — and architect products around data and models from day one. The difference shows up as speed, consistency, and lower defect rates.'
    },
    {
      question: 'What services does an AI-first company like Kalpita offer?',
      answer: 'Kalpita Technologies offers nine service lines: custom software development, web development, mobile app development, UI/UX design, data engineering, testing & QA, IT consulting, AI as a Service, and digital marketing. Alongside services, Kalpita ships its own offerings — Kalpita Nexa and Kalpita AI Interview (solutions) and Kalpita Recruit (product). The portfolio now extends to six more, born from continuous hackathons and R&D — Kalpita CodeGuard, Kalpita KrishiAI, Kalpita MoodFit, Kalpita PromptOps and Kalpita SprintMind (products), and Kalpita Sentinel (a solution).'
    },
    {
      question: 'What are the benefits of AI-first software solutions for businesses?',
      answer: 'Measured outcomes, not abstractions: 30–50% faster delivery, 40–50% lower QA effort, and documented client results such as a 30% efficiency gain from a RAG-based deployment and 100% billing accuracy from automation. AI-first systems also adapt faster — they are designed to absorb new models and data without re-architecture.'
    },
    {
      question: 'How do I choose an AI-first technology partner for my enterprise?',
      answer: 'Check four things: proof of AI in their delivery process (not just marketing), verifiable security credentials (SOC 2, ISO), domain depth in your industry, and named client outcomes. Kalpita Technologies offers all four — 8+ years of delivery across Insurance, Finance, and Healthcare, with certifications and client testimonials on record. Insist on proof you can verify: Clutch 4.7, Google 4.7, Glassdoor 4.5, SOC 2, ISO, Great Place to Work.'
    },
    {
      question: 'Which industries does Kalpita Technologies serve?',
      answer: 'Primary depth in InsurTech, FinTech, and HealthTech, with proven delivery in E-commerce, EdTech, Media & Entertainment, and Hi-Tech. Regulated industries are our home turf: HIPAA-aligned healthcare work, GDPR-aligned data handling, and SOC 2-certified processes.'
    },
    {
      question: 'Where is Kalpita Technologies located?',
      answer: 'Kalpita Technologies operates from Bengaluru, India (HQ, HRBR Layout) and Austin, Texas, USA. Delivery teams align to PST, CST, and EST working hours, giving US clients real-time collaboration with nearshore economics.'
    }
  ];

  industriesVisible = false;
  servicesVisible = false;
  metricsVisible = false;

  private jsonLdScript: any;

  // Pagination vars
  paginatedIndustries: Industry[][] = [];
  templateItems: TemplateItem[] = [];
  activeSlideIndex = 0;
  private slideInterval: any;

  paginatedServices: Service[][] = [];
  templateServiceItems: ServiceTemplateItem[] = [];
  activeServiceSlideIndex = 0;
  private serviceSlideInterval: any;

  activeSection: string = 'hero';
  hoveredSection: string | null = null;
  hoveredIndustryIndex: number | null = null;
  hoveredServiceIndex: number | null = null;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private _renderer: Renderer2,
    private route: ActivatedRoute,
    private router: Router,
    private contactModal: ContactModalService
  ) { }

  navigateTo(path: string): void {
    if (path) {
      const cleanPath = path.startsWith('/') ? path.substring(1) : path;
      const routeSegments = ['/', ...cleanPath.split('/')];

      this.router.navigate(routeSegments).then(success => {
        if (success && isPlatformBrowser(this.platformId)) {
          window.scrollTo(0, 0);
        } else if (!success) {
          console.error('Navigation failed. Check route config for:', cleanPath);
        }
      });
    }
  }

  ngOnInit(): void {
    this.injectSchemaMarkup();

    // 1. Prepare Industries Pagination
    for (let i = 0; i < this.allIndustries.length; i += 3) {
      this.paginatedIndustries.push(this.allIndustries.slice(i, i + 3));
    }
    // Set initial data with placeholders
    const firstIndustryPage = this.paginatedIndustries[0];
    const industryTemplateItems: TemplateItem[] = [...firstIndustryPage];
    const industryPlaceholders = 3 - firstIndustryPage.length;
    for (let i = 0; i < industryPlaceholders; i++) {
      industryTemplateItems.push({ placeholder: true });
    }
    this.templateItems = industryTemplateItems;

    // 2. Prepare Services Pagination
    for (let i = 0; i < this.allServices.length; i += 3) {
      this.paginatedServices.push(this.allServices.slice(i, i + 3));
    }
    // Set initial data with placeholders
    const firstServicePage = this.paginatedServices[0];
    const serviceTemplateItems: ServiceTemplateItem[] = [...firstServicePage];
    const servicePlaceholders = 3 - firstServicePage.length;
    for (let i = 0; i < servicePlaceholders; i++) {
      serviceTemplateItems.push({ placeholder: true });
    }
    this.templateServiceItems = serviceTemplateItems;

    // Fragment Scroll Listener
    this.route.fragment.subscribe((fragment) => {
      if (fragment) {
        this.loadedSections.add(fragment);
        setTimeout(() => {
          this.scrollToSection(fragment);
        }, 100);
      }
    });
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    setTimeout(() => {
      const video = this.heroVideo?.nativeElement;
      if (video) {
        video.muted = true;
        video.playsInline = true;
        video.load();
        (typeof video.play === 'function' ? video.play() : Promise.resolve()).catch(() => {
          console.warn('Autoplay blocked – retrying...');
          (typeof video.play === 'function' ? video.play() : Promise.resolve());
        });
      }
    }, 100);

    if ('IntersectionObserver' in window) {
      this.setupLazyLoadObserver();
      this.setupAnimationObserver();
      this.setupVideoObserver();
      this.setupNavigationObserver();
    } else {
      // Fallback for older browsers
      this.sections.forEach(s => this.loadedSections.add(s.id));
      this.industriesVisible = true;
      this.servicesVisible = true;
      this.metricsVisible = true;
      this.startAutoSlide();
      this.startAutoServiceSlide();
    }
  }

  // 1. Lazy Load Observer (Buffer for loading heavy components)
  setupLazyLoadObserver() {
    const options = {
      root: null,
      threshold: 0.35,
      rootMargin: '0px 0px -20% 0px'
    };

    this.lazyLoadObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          if (id === 'testimonialsObserverTarget') this.loadedSections.add('testimonials');
          if (id === 'technologyObserverTarget') this.loadedSections.add('technology');
          if (id === 'recentupdatesObserverTarget') this.loadedSections.add('recentupdates');
          if (id === 'galleryObserverTarget') this.loadedSections.add('gallery');

          this.lazyLoadObserver?.unobserve(entry.target);
        }
      });
    }, options);

    const targets = [
      'testimonialsObserverTarget', 'technologyObserverTarget',
      'recentupdatesObserverTarget', 'galleryObserverTarget'
    ];

    targets.forEach(t => {
      const el = document.getElementById(t);
      if (el) this.lazyLoadObserver?.observe(el);
    });
  }

  // 2. Animation & Slider Reset Observer (FIXED VERSION)
  setupAnimationObserver() {
    const options = {
      root: null,
      threshold: 0.15,
      rootMargin: '50px 0px 50px 0px' // Buffer zone to prevent flickering
    };

    this.animationObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const id = entry.target.id;

        if (entry.isIntersecting) {

          // --- INDUSTRIES SECTION ---
          if (id === 'industries') {
            if (!this.industriesVisible) {
              this.industriesVisible = true;
              setTimeout(() => {
                this.selectSlide(0);
              }, 150);
            } else if (!this.slideInterval) {
              // Resume if paused
              this.startAutoSlide();
            }
          }

          // --- SERVICES SECTION ---
          if (id === 'services') {
            if (!this.servicesInitialized) {
              this.servicesInitialized = true;
              this.servicesVisible = true;
              setTimeout(() => {
                this.selectServiceSlide(0);
                this.startAutoServiceSlide();
              }, 150);
            } else if (!this.serviceSlideInterval) {
              // Resume if paused
              this.startAutoServiceSlide();
            }
          }

          // --- METRICS TRIGGER ---
          if (id === 'metrics-trigger') {
            if (!this.metricsVisible) {
              this.metricsVisible = true;
            }
          }

        } else {
          // When elements leave viewport - ONLY pause timers, NEVER hide
          
          if (id === 'industries') {
            this.clearAutoSlide();
          }
          
          if (id === 'services') {
            this.clearAutoServiceSlide();
          }
          
          // IMPORTANT: Don't hide metrics-trigger anymore
          // Keep it visible once shown to prevent re-animation
        }
      });
    }, options);

    const targets = ['industries', 'services', 'metrics-trigger'];

    targets.forEach(t => {
      const el = document.getElementById(t);
      if (el) this.animationObserver?.observe(el);
    });
  }

  // 3. Video Observer
  setupVideoObserver() {
    if (!this.milestoneVideo?.nativeElement) return;

    const options = {
      root: null,
      threshold: 0.35,
      rootMargin: '0px 0px -20% 0px'
    };

    this.videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = this.milestoneVideo.nativeElement;
        if (entry.isIntersecting) {
          video.muted = true;
          video.currentTime = 0;
          (typeof video.play === 'function' ? video.play() : Promise.resolve()).catch(e => console.error("Video play failed", e));
        } else {
          video.pause();
        }
      });
    }, options);

    this.videoObserver.observe(this.milestoneVideo.nativeElement);
  }

  // 4. Navigation Observer
  setupNavigationObserver() {
    const options = {
      root: null,
      threshold: 0.35,
      rootMargin: '0px 0px -20% 0px'
    };

    this.navigationObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          const sectionMap: { [key: string]: string } = {
            'heroObserverTarget': 'hero',
            'clientsObserverTarget': 'clients',
            'testimonialsObserverTarget': 'testimonials',
            'milestonesObserverTarget': 'milestones',
            'industries': 'industries',
            'technologyObserverTarget': 'technology',
            'about': 'about',
            'metrics-trigger': 'about',
            'services': 'services',
            'recentupdatesObserverTarget': 'recentupdates',
            'galleryObserverTarget': 'gallery'
          };

          const sectionId = sectionMap[id] || id;

          if (this.sections.some(s => s.id === sectionId)) {
            this.activeSection = sectionId;
          }
        }
      });
    }, options);

    const navTargets = [
      'heroObserverTarget', 'clientsObserverTarget', 'testimonialsObserverTarget',
      'milestonesObserverTarget', 'industries', 'technologyObserverTarget',
      'about', 'services', 'recentupdatesObserverTarget', 'galleryObserverTarget'
    ];

    navTargets.forEach(t => {
      const el = document.getElementById(t);
      if (el) this.navigationObserver?.observe(el);
    });
  }

  shouldLoadSection(sectionId: string): boolean {
    return this.loadedSections.has(sectionId);
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    this.clearAutoSlide();
    this.clearAutoServiceSlide();
    if (this.lazyLoadObserver) this.lazyLoadObserver.disconnect();
    if (this.animationObserver) this.animationObserver.disconnect();
    if (this.videoObserver) this.videoObserver.disconnect();
    if (this.navigationObserver) this.navigationObserver.disconnect();
  }

injectSchemaMarkup(): void {
    // Prerendering and client-side hydration both run ngOnInit, and this
    // schema script is appended outside Angular's view tree (so hydration
    // never reconciles it) -- without this, every page ends up with the
    // schema duplicated once the client bootstraps over the prerendered HTML.
    Array.from(this._document.head.querySelectorAll('script[type="application/ld+json"]')).forEach((el: any) => this._renderer.removeChild(this._document.head, el));
    const fullGraphSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://www.kalpitatechnologies.com/#webpage",
      "url": "https://www.kalpitatechnologies.com/",
      "name": "AI-First Software Development Company",
      "description": "Kalpita Technologies is an AI-first software development company in Bengaluru & Austin. 130+ engineers, 70%+ AI-enabled delivery, SOC 2 & ISO certified.",
      "inLanguage": "en",
      "datePublished": "2026-07-03",
      "dateModified": "2026-07-03",
      "isPartOf": {
        "@type": "WebSite",
        "@id": "https://www.kalpitatechnologies.com/#website",
        "name": "Kalpita Technologies",
        "url": "https://www.kalpitatechnologies.com/"
      },
      "breadcrumb": {
        "@id": "https://www.kalpitatechnologies.com/#breadcrumb"
      },
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": [
          ".hero-heading",
          ".hero-subheading",
          ".content-title",
          ".content-text",
          ".section-header h2"
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "Kalpita Technologies",
        "@id": "https://www.kalpitatechnologies.com/#organization"
      }
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://www.kalpitatechnologies.com/#breadcrumb",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": {
            "@type": "WebPage",
            "@id": "https://www.kalpitatechnologies.com/",
            "name": "Home",
            "url": "https://www.kalpitatechnologies.com/"
          }
        }
      ]
    },
    {
      "@type": "Article",
      "@id": "https://www.kalpitatechnologies.com/#article",
      "headline": "The AI-First Software Development Company in India & the USA",
      "name": "The AI-First Software Development Company in India & the USA",
      "description": "Kalpita Technologies is an AI-first software development company in Bengaluru & Austin. 130+ engineers, 70%+ AI-enabled delivery, SOC 2 & ISO certified.",
      "url": "https://www.kalpitatechnologies.com/",
      "inLanguage": "en",
      "author": {
        "@type": "Organization",
        "name": "Kalpita Technologies",
        "url": "https://www.kalpitatechnologies.com/"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Kalpita Technologies",
        "@id": "https://www.kalpitatechnologies.com/#organization",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
        }
      },
      "datePublished": "2026-07-03",
      "dateModified": "2026-07-03",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://www.kalpitatechnologies.com/#webpage",
        "name": "The AI-First Software Development Company in India & the USA",
        "url": "https://www.kalpitatechnologies.com/"
      },
      "keywords": [
        "AI-first software development company",
        "What is an AI-first software development company",
        "Best AI-first software development companies in India",
        "AI-first approach in software development",
        "What services does an AI-first company offer",
        "AI-first technology partner for enterprise",
        "Kalpita Technologies",
        "Kalpita Nexa",
        "Kalpita AI Interview",
        "Kalpita Recruit",
        "Kalpita Sentinel",
        "CodeGuardAI",
        "PromptOps AI",
        "SprintMind",
        "sovereign AI",
        "enterprise AI solutions",
        "AI products and solutions",
        "custom software development company India and USA",
        "SOC 2 certified software development company",
        "70% AI-enabled engineering"
      ],
      "articleBody": "Last Updated: June 2026 Kalpita Technologies is an AI-first software development company in Bengaluru and Austin — 130+ engineers, 70%+ AI-enabled engineering, SOC 2, and ISO certified. Most enterprises don't have a software problem. They have a speed problem. Ideas wait in backlogs while markets move. Kalpita closes that gap: we build custom software with AI embedded across the entire development lifecycle — so your releases ship 30–50% faster, with security wired in from the first commit. And the in-house specialists who scope your project are the ones who build it — not a rotating bench. Solution mindset. Product company. Always AI-first. Get a Free Quote → What Our Clients Say \"I wanted to take a moment to thank you for the services your team has provided. I am really impressed by the quality of services I received from Kalpita Technologies (P) Ltd. You were right on schedule, charged reasonable prices, were professional and courteous in dealings, and delivered items well before time. It is rare to find a service provider with such professional consistency — you are a valued service provider to our business, and I wouldn't hesitate in recommending you to other companies.\" — Nouri Bakkali, Chief Executive Officer at Diligencia \"When I first heard about Kalpita through a friend, I wasn't sure of the quality of work, being a small company. But the moment I started working with them, I realized that they are very determined and dedicated towards whatever they do. Their main strength is on .NET software projects, and they excel in it. When Omnisite was in desperate need of an embedded engineer, Kalpita went out of their way to hire one, even though they had no prior experience in the field, just to help us in the needy time. We are grateful to have this relationship with Kalpita. I 100% recommend Kalpita for your outsourcing needs. Thanks again Kalpita for all the great work and service provided.\" — Avinash R. Setty, CTO, Omnisite \"Having worked with Kalpita on multiple projects on multiple platforms, it always felt like an extended team rather than an external service provider. Like your win-win proposition. The biggest strength of this team has been striking a balance between high level design/planning and attention to details at the lowest level while executing, a rare combo. Every single time, the deliverables hit the servers on/before the proposed timeline with the highest quality. Needless to say, would strongly recommend Kalpita for any software service needs. Looking forward to collaborating again on future projects and extending our journey together. Thanks for everything.\" — Rajesh N. Krishnan, CEO, Brightwave Systems LLC \"It is a great journey with Kalpita Technologies for years. The Kalpita team is very professional. I always wonder how everyone in Kalpita is highly communicative. They are clear on their message and make sure they understand us well. Kalpita did its best professionally during tough times like covid. Outstanding technologies, the best execution methodologies, and effective team handling skills. I really admire on their grow together attitude, clear vision, great business model and giving their best efforts to achieve them. Wish Kalpita prosper exponentially in the coming years.\" — Karunamoorthy Karuppanan, CEO, Infinijith Apps & Technologies PVT LTD \"We at Nouveau Labs were building next generation audio and video platform and were in need of people with right skillset and great attitude on App development. Kalpita Technologies helped us instantly for the same and the best part of the engagement was, they went beyond their limits and were very flexible to accommodate changing needs. Definitely Kalpita Technologies has strong belief in 'Customer First'. It's a great journey with Kalpita for more than a year and we wish them a great success ahead.\" — Shrikanth Janagonda, Director of Engineering, Nouveau Labs \"Working with the Kalpita Technologies team was a great experience. They demonstrated excellent knowledge, dedication, and commitment to delivering our project on time with exceptional quality. The Vehicle Delivery System they built helped automate the entire delivery process, from dealer to customer once a vehicle is booked online. What I appreciate most about Kalpita is their quality of work, timely delivery, and proactive response. Their team often goes the extra mile, even working over weekends to ensure deadlines are met. They're truly awesome and always available when needed. I would highly recommend Kalpita Technologies for any kind of technology development or customized software solution, no matter what the technology, Kalpita will make it work. Overall, we found the Kalpita team to be knowledgeable, dedicated, and committed, and we look forward to collaborating with them again.\" — Shanker Agarwal, Founder & CEO, Dataabsolute Technologies \"Our collaboration with Kalpita Technologies was nothing short of exceptional. Their team brought a rare blend of technical excellence, accountability, and agility that truly elevated our projects. We were impressed by how seamlessly they integrated with our internal team, consistently delivering high-quality work, often ahead of schedule. Their clarity in communication, responsiveness to change, and commitment to doing things right made the entire engagement smooth and efficient. What stood out most was their professionalism and strong sense of business ethics, which built lasting trust. Overall, we're extremely satisfied with Kalpita Technologies and would gladly collaborate with them again for future initiatives.\" — Janakarathinam K.K, Co-Founder & Director, Qono Technologies Pvt Ltd Measurable Success Metric. Proof 30+. Apps built 8+. Years of expertise 130+. Skilled engineers 70+. Global clients 100+. Projects delivered 38%. Annual team growth Industry Expertise Tailored to Your Business Needs Cross-industry delivery is where AI-first engineering proves itself. We apply AI, automation, and modern software architecture to the verticals where the stakes — and the compliance bar — are highest. InsurTech AI-driven insurance platforms that automate policy workflows, accelerate claims, and lift policyholder experience. For Smart Insure, onboarding efficiency rose 30%. Explore InsurTech → FinTech Secure financial software that improves data accuracy, automates reporting, and sharpens decision-making — proven on enterprise data warehouse and Power BI reporting engagements. Explore FinTech → HealthTech Compliant healthcare systems that simplify patient care and clinical operations — including the ARC engine that centralized 18 internal and 14 third-party applications on AWS. Explore HealthTech → E-commerce High-performance commerce platforms with smart automation and secure integrations — from end-to-end SaaS storefronts to multi-region retail mobile apps. Explore E-commerce → Media & Entertainment OTT, streaming, and content platforms engineered for speed, scale, and audience engagement. Explore Media & Entertainment → Hi-Tech AI, automation, and cloud engineering for product companies that need to ship faster than their category. Explore Hi-Tech → EdTech Interactive learning platforms with personalized learning paths, analytics, and digital classroom experiences. Explore EdTech → Our Technology Stack for Enterprise-Grade Solutions One stack does not fit every enterprise — so we select per engagement. Frontend: Angular, React, Vue. Backend: .NET Core, Java, Node.js. AI/ML: Python, RAG pipelines, and a Multi-LLM Strategy spanning Claude, GPT, and Gemini, with MCP integrations and GitHub Copilot deployed org wide. Mobile: React Native, Flutter, native iOS, and Android. QA: Espresso, Ansible, Postman, Azure DevOps, Selenium, Cypress, Playwright. DevOps: Kubernetes, Jenkins, GitLab, Docker, OpenShift. Cloud: AWS, Azure, GCP. Data: Power BI, Tableau, Looker, Informatica, ADF, SSIS, SSAS. Why Choose Kalpita as Your AI Software Development Partner? An AI-first software development company embeds AI into how software is conceived, written, tested, and run — not as a feature bolted on at the end. At Kalpita Technologies, 70%+ of engineering is AI-enabled across SDLC, and that approach extends into our own portfolio: Kalpita Nexa and Kalpita AI Interview (enterprise AI solutions) and Kalpita Recruit (an AI-native ATS product). Fast because it is disciplined, not rushed — three-tier governance keeps that speed predictable. Nearshore and near-heart delivery — teams aligned to PST/CST/EST for the USA, with engineers embedded in your culture and goals 30–50% faster delivery through AI-enabled engineering and a Pool Engineering Model with 100% allocation Agile, secure, and disciplined — three-tier governance with documented escalation, from daily delivery forums to quarterly executive steering GEO & AEO-optimized, future-ready architecture — software built to be found and cited in the AI-search era DevSecOps-first — security embedded across the SDLC, not audited at the end AI-driven security — proactive threat detection and risk mitigation, run by security-trained engineers GDPR-aligned Privacy by Design — data privacy engineered in from the first requirement Proof, not promises — SOC 2 (AICPA), ISO certified, Great Place to Work, Clutch 4.7, Google 4.7 Explore About Us → Proven Outcomes Across Industries 30% Higher Efficiency — InsurTech For Smart Insure, we re-engineered policy onboarding and customer engagement: operational efficiency up 30%, conversion rates up 10–15%. Faster, more accurate, customer-first insurance workflows. Read the case study → 50% Faster Reports — FinTech For a US retirement plan platform, we improved data accuracy and cut errors by 30%, while financial advisors generate customized reports 50% faster — turning month-end reporting into same-day insight. Read the case study → 100% Billing Accuracy — InsurTech An Azure-powered automated billing system with real-time updates and notifications now runs billing management end to end — 100% accuracy, hours saved every week, human error designed out. Read the case study → Explore Our Software Development Services Nine service lines. One standard: secure, scalable software that moves a business metric. Custom Software Development Purpose-built software for the workflows off-the-shelf tools can't handle — engineered for scale, efficiency, and long-term growth. Explore Custom Software Development → Web Development Responsive, secure, SEO- and AEO-optimized websites and web apps that turn your online presence into a growth channel. Explore Web Development → Mobile App Development High-performance iOS and Android apps — native and cross-platform — that users keep and businesses measure. Explore Mobile App Development → UI/UX Design Interfaces designed around the person using them: clear, intuitive, conversion aware. Explore UI/UX Design → Data Engineering Pipelines, warehouses, and BI dashboards that make your data accurate, accessible, and decision ready. Explore Data Engineering → Testing & QA End-to-end quality engineering that reduces QA effort by 40–50% while protecting every release. Explore Testing & QA → IT Consulting Technology strategy, modernization roadmaps, and architecture guidance from engineers who build what they recommend. Explore IT Consulting → AI as a Service RAG pipelines, agentic workflows, NLP, and computer vision — production AI, deployed where your data lives. Explore AI as a Service → Digital Marketing SEO, AEO, and performance marketing that make your brand visible in both classic search and AI answers. Explore Digital Marketing → Read Our Latest Tech Insights and Success Stories Practical engineering knowledge from the team that ships it: Agentic AI, RAG architecture, DevOps, data visualization, and the trends reshaping enterprise software. Visit the Blog → Experience Kalpita Through Our Video Gallery Step inside Kalpita — product walkthroughs, culture stories, and the people behind the work. Watch the Videos → Frequently Asked Questions What is an AI-first software development company? An AI-first software development company embeds AI into every phase of the software lifecycle — requirements, coding, testing, deployment, and operations — instead of treating AI as an add-on feature. Kalpita Technologies works this way by default: 70%+ of our engineering is AI-enabled, using a Multi-LLM Strategy, RAG pipelines, and AI-assisted development tooling. What is the AI-first approach in software development? The AI-first approach means designing systems with intelligence built in, not bolted on. In practice: AI-assisted coding and testing, retrieval-augmented architectures that ground outputs in your data, and agentic workflows that automate multi-step processes. The result is 30–50% faster delivery and software that improves with use. How do AI-first companies build software differently from traditional firms? Traditional firms write, test, and document software manually, then consider AI features later. AI-first companies like Kalpita use AI at every step — code generation with human review, automated test design, AI-driven security scanning — and architect products around data and models from day one. The difference shows as speed, consistency, and lower defect rates. What services does an AI-first company like Kalpita offer? Kalpita Technologies offers nine service lines: custom software development, web development, mobile app development, UI/UX design, data engineering, testing & QA, IT consulting, AI as a Service, and digital marketing. Alongside services, Kalpita ships its own offerings — Kalpita Nexa and Kalpita AI Interview (solutions) and Kalpita Recruit (product). Recruit (product). The portfolio now extends to six more, born from continuous hackathons and R&D — Kalpita CodeGuard, Kalpita KrishiAI, Kalpita MoodFit, Kalpita PromptOps and Kalpita SprintMind (products), and Kalpita Sentinel (a solution). What are the benefits of AI-first software solutions for businesses? Measured outcomes, not abstractions: 30–50% faster delivery, 40–50% lower QA effort, and documented client results such as a 30% efficiency gain from a RAG-based deployment and 100% billing accuracy from automation. AI-first systems also adapt faster — they are designed to absorb new models and data without re-architecture. How do I choose an AI-first technology partner for my enterprise? Check four things: proof of AI in their delivery process (not just marketing), verifiable security credentials (SOC 2, ISO), domain depth in your industry, and named client outcomes. Kalpita Technologies offers all four — 8+ years of delivery across Insurance, Finance, and Healthcare, with certifications and client testimonials on record. Insist on proof you can verify: Clutch 4.7, Google 4.7, Glassdoor 4.5, SOC 2, ISO, Great Place to Work. Which industries do Kalpita Technologies serve? Primary depth in InsurTech, FinTech, and HealthTech, with proven delivery in E-commerce, EdTech, Media & Entertainment, and Hi-Tech. Regulated industries are our home turf: HIPAA-aligned healthcare work, GDPR-aligned data handling, and SOC 2-certified processes. Where is Kalpita Technologies located? Kalpita Technologies operates from Bengaluru, India (HQ, HRBR Layout) and Austin, Texas, USA. Delivery teams align to PST, CST, and EST working hours, giving US clients real-time collaboration with near-shore economics. Tell us your challenge. We'll show you the AI-first way to solve it — Get a Free Quote → Connect@kalpitatechnologies.com",
      "about": [
        {
          "@type": "Organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/"
        }
      ],
      "additionalProperty": [
        {
          "@type": "PropertyValue",
          "name": "Primary keyword",
          "value": "AI-first software development company"
        },
        {
          "@type": "PropertyValue",
          "name": "Buyer intent",
          "value": "Solution-aware"
        },
        {
          "@type": "PropertyValue",
          "name": "TL;DR",
          "value": "Kalpita Technologies is an AI-first software development company in Bengaluru & Austin. 130+ engineers, 70%+ AI-enabled delivery, SOC 2 & ISO certified."
        }
      ]
    },
    {
      "@type": "ItemList",
      "@id": "https://www.kalpitatechnologies.com/#sections",
      "name": "Page sections",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Article",
            "@id": "https://www.kalpitatechnologies.com/#what-our-clients-say",
            "name": "What Our Clients Say",
            "url": "https://www.kalpitatechnologies.com/#what-our-clients-say"
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "Article",
            "@id": "https://www.kalpitatechnologies.com/#measurable-success",
            "name": "Measurable Success",
            "url": "https://www.kalpitatechnologies.com/#measurable-success"
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "Article",
            "@id": "https://www.kalpitatechnologies.com/#industry-expertise-tailored-to-your-business-needs",
            "name": "Industry Expertise Tailored to Your Business Needs",
            "url": "https://www.kalpitatechnologies.com/#industry-expertise-tailored-to-your-business-needs"
          }
        },
        {
          "@type": "ListItem",
          "position": 4,
          "item": {
            "@type": "Article",
            "@id": "https://www.kalpitatechnologies.com/#insurtech",
            "name": "InsurTech",
            "url": "https://www.kalpitatechnologies.com/#insurtech"
          }
        },
        {
          "@type": "ListItem",
          "position": 5,
          "item": {
            "@type": "Article",
            "@id": "https://www.kalpitatechnologies.com/#fintech",
            "name": "FinTech",
            "url": "https://www.kalpitatechnologies.com/#fintech"
          }
        },
        {
          "@type": "ListItem",
          "position": 6,
          "item": {
            "@type": "Article",
            "@id": "https://www.kalpitatechnologies.com/#healthtech",
            "name": "HealthTech",
            "url": "https://www.kalpitatechnologies.com/#healthtech"
          }
        },
        {
          "@type": "ListItem",
          "position": 7,
          "item": {
            "@type": "Article",
            "@id": "https://www.kalpitatechnologies.com/#e-commerce",
            "name": "E-commerce",
            "url": "https://www.kalpitatechnologies.com/#e-commerce"
          }
        },
        {
          "@type": "ListItem",
          "position": 8,
          "item": {
            "@type": "Article",
            "@id": "https://www.kalpitatechnologies.com/#media-entertainment",
            "name": "Media & Entertainment",
            "url": "https://www.kalpitatechnologies.com/#media-entertainment"
          }
        }
      ]
    },
    {
      "@type": "HowTo",
      "@id": "https://www.kalpitatechnologies.com/#howto",
      "name": "How Kalpita Technologies delivers: The AI-First Software Development Company in India & the USA",
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Measurable Success",
          "url": "https://www.kalpitatechnologies.com/#measurable-success"
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Industry Expertise Tailored to Your Business Needs",
          "url": "https://www.kalpitatechnologies.com/#industry-expertise-tailored-to-your-business-needs"
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "InsurTech",
          "url": "https://www.kalpitatechnologies.com/#insurtech"
        },
        {
          "@type": "HowToStep",
          "position": 4,
          "name": "FinTech",
          "url": "https://www.kalpitatechnologies.com/#fintech"
        },
        {
          "@type": "HowToStep",
          "position": 5,
          "name": "HealthTech",
          "url": "https://www.kalpitatechnologies.com/#healthtech"
        },
        {
          "@type": "HowToStep",
          "position": 6,
          "name": "E-commerce",
          "url": "https://www.kalpitatechnologies.com/#e-commerce"
        }
      ]
    },
    {
      "@type": "FAQPage",
      "@id": "https://www.kalpitatechnologies.com/#faq",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is an AI-first software development company?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "An AI-first software development company embeds AI into every phase of the software lifecycle — requirements, coding, testing, deployment, and operations — instead of treating AI as an add-on feature. Kalpita Technologies works this way by default: 70%+ of our engineering is AI-enabled, using a Multi-LLM Strategy, RAG pipelines, and AI-assisted development tooling."
          }
        },
        {
          "@type": "Question",
          "name": "What is the AI-first approach in software development?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The AI-first approach means designing systems with intelligence built in, not bolted on. In practice: AI-assisted coding and testing, retrieval-augmented architectures that ground outputs in your data, and agentic workflows that automate multi-step processes. The result is 30–50% faster delivery and software that improves with use."
          }
        },
        {
          "@type": "Question",
          "name": "How do AI-first companies build software differently from traditional firms?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Traditional firms write, test, and document software manually, then consider AI features later. AI-first companies like Kalpita use AI at every step — code generation with human review, automated test design, AI-driven security scanning — and architect products around data and models from day one. The difference shows up as speed, consistency, and lower defect rates."
          }
        },
        {
          "@type": "Question",
          "name": "What services does an AI-first company like Kalpita offer?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Kalpita Technologies offers nine service lines: custom software development, web development, mobile app development, UI/UX design, data engineering, testing & QA, IT consulting, AI as a Service, and digital marketing. Alongside services, Kalpita ships its own offerings — Kalpita Nexa and Kalpita AI Interview (solutions) and Kalpita Recruit (product). The portfolio now extends to six more, born from continuous hackathons and R&D — Kalpita CodeGuard, Kalpita KrishiAI, Kalpita MoodFit, Kalpita PromptOps and Kalpita SprintMind (products), and Kalpita Sentinel (a solution)."
          }
        },
        {
          "@type": "Question",
          "name": "What are the benefits of AI-first software solutions for businesses?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Measured outcomes, not abstractions: 30–50% faster delivery, 40–50% lower QA effort, and documented client results such as a 30% efficiency gain from a RAG-based deployment and 100% billing accuracy from automation. AI-first systems also adapt faster — they are designed to absorb new models and data without re-architecture."
          }
        },
        {
          "@type": "Question",
          "name": "How do I choose an AI-first technology partner for my enterprise?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Check four things: proof of AI in their delivery process (not just marketing), verifiable security credentials (SOC 2, ISO), domain depth in your industry, and named client outcomes. Kalpita Technologies offers all four — 8+ years of delivery across Insurance, Finance, and Healthcare, with certifications and client testimonials on record. Insist on proof you can verify: Clutch 4.7, Google 4.7, Glassdoor 4.5, SOC 2, ISO, Great Place to Work."
          }
        }
      ]
    },
    {
      "@type": "Organization",
      "@id": "https://www.kalpitatechnologies.com/#organization",
      "name": "Kalpita Technologies",
      "legalName": "Kalpita Technologies Private Limited",
      "url": "https://www.kalpitatechnologies.com/",
      "logo": {
        "@type": "ImageObject",
        "@id": "https://www.kalpitatechnologies.com/#logo",
        "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png",
        "contentUrl": "https://www.kalpitatechnologies.com/assets/common/black-logo.png",
        "caption": "Kalpita Technologies Logo"
      },
      "description": "Kalpita Technologies is an AI-first technology company combining a solution-led delivery mindset with a growing portfolio of proprietary AI products and solutions — nine live offerings today (five solutions, four products): Kalpita Nexa, Kalpita AI Interview, Kalpita Sentinel, PromptOps AI, and SprintMind (solutions); Kalpita Recruit, CodeGuardAI, MoodFit, and KrishiAI (products). Headquartered in Bengaluru, India, with a US office in Austin, Texas, Kalpita's 130+ specialists bring 8+ years of services excellence — SOC 2 and ISO certified, 70%+ AI-enabled engineering — serving InsurTech, FinTech, HealthTech, EdTech, E-commerce, Hi-Tech, and Media & Entertainment enterprises. Solution mindset. Product company. Always AI-first.",
      "slogan": "Make the impossible, possible.",
      "foundingDate": "2018",
      "email": "connect@kalpitatechnologies.com",
      "telephone": "+91-87620-33990",
      "sameAs": [
        "https://www.linkedin.com/company/kalpita-technologies/",
        "https://www.instagram.com/kalpitatechnologies/",
        "https://x.com/KalpitaTech",
        "https://www.facebook.com/KalpitaTechnologies/",
        "https://www.youtube.com/@KalpitaTechnologies"
      ],
      "address": [
        {
          "@type": "PostalAddress",
          "streetAddress": "4M 417/A, 4th Floor, HRBR Layout 3rd Block, Kammanahalli Main Road",
          "addressLocality": "Bengaluru",
          "addressRegion": "Karnataka",
          "postalCode": "560043",
          "addressCountry": "IN"
        },
        {
          "@type": "PostalAddress",
          "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
          "addressLocality": "Austin",
          "addressRegion": "TX",
          "postalCode": "78746",
          "addressCountry": "US"
        }
      ],
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+91-87620-33990",
          "contactType": "customer service",
          "email": "connect@kalpitatechnologies.com",
          "areaServed": "IN"
        },
        {
          "@type": "ContactPoint",
          "telephone": "+1-512-768-5686",
          "contactType": "customer service",
          "email": "connect@kalpitatechnologies.com",
          "areaServed": "US"
        }
      ],
      "knowsAbout": [
        "AI-first software development",
        "Agentic AI",
        "Retrieval-Augmented Generation (RAG)",
        "Sovereign AI",
        "AI interview fraud detection",
        "Kalpita Nexa",
        "Kalpita AI Interview",
        "Kalpita Recruit",
        "Kalpita Sentinel",
        "PromptOps AI",
        "SprintMind",
        "CodeGuardAI",
        "MoodFit",
        "KrishiAI"
      ]
    },
    {
      "@type": "LocalBusiness",
      "@id": "https://www.kalpitatechnologies.com/#localbusiness",
      "name": "Kalpita Technologies Inc.",
      "image": "https://www.kalpitatechnologies.com/assets/common/black-logo.png",
      "url": "https://www.kalpitatechnologies.com/",
      "telephone": "+1-512-768-5686",
      "email": "connect@kalpitatechnologies.com",
      "priceRange": "$$",
      "description": "Kalpita Technologies is an AI-first technology company combining a solution-led delivery mindset with a growing portfolio of proprietary AI products and solutions — nine live offerings today (five solutions, four products): Kalpita Nexa, Kalpita AI Interview, Kalpita Sentinel, PromptOps AI, and SprintMind (solutions); Kalpita Recruit, CodeGuardAI, MoodFit, and KrishiAI (products). Headquartered in Bengaluru, India, with a US office in Austin, Texas, Kalpita's 130+ specialists bring 8+ years of services excellence — SOC 2 and ISO certified, 70%+ AI-enabled engineering — serving InsurTech, FinTech, HealthTech, EdTech, E-commerce, Hi-Tech, and Media & Entertainment enterprises. Solution mindset. Product company. Always AI-first.",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
        "addressLocality": "Austin",
        "addressRegion": "TX",
        "postalCode": "78746",
        "addressCountry": "US"
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday"
        ],
        "opens": "09:00",
        "closes": "18:00"
      },
      "sameAs": [
        "https://www.linkedin.com/company/kalpita-technologies/",
        "https://www.instagram.com/kalpitatechnologies/",
        "https://x.com/KalpitaTech",
        "https://www.facebook.com/KalpitaTechnologies/",
        "https://www.youtube.com/@KalpitaTechnologies"
      ]
    }
  ]
};

    this.jsonLdScript = this._renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(fullGraphSchema);
    this._renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this._renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }

  // --- INDUSTRIES SLIDER (FIXED) ---
  startAutoSlide(): void {
    // Don't start if already running or if section not visible
    if (this.slideInterval || !this.industriesVisible) return;
    
    this.slideInterval = setInterval(() => {
      const nextSlideIndex = (this.activeSlideIndex + 1) % this.paginatedIndustries.length;
      this.selectSlide(nextSlideIndex);
    }, 6000);
  }

  clearAutoSlide(): void {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
      this.slideInterval = null;
    }
  }

  selectSlide(index: number): void {
    this.hoveredIndustryIndex = null;
    this.activeSlideIndex = index;
    const currentPage = this.paginatedIndustries[index];

    if (currentPage) {
      const newTemplateItems: TemplateItem[] = [...currentPage];
      const placeholdersNeeded = 3 - currentPage.length;
      if (placeholdersNeeded > 0) {
        for (let i = 0; i < placeholdersNeeded; i++) {
          newTemplateItems.push({ placeholder: true });
        }
      }
      this.templateItems = newTemplateItems;
    }

    // DON'T restart the interval here - it creates conflicts
    // The interval is already running from startAutoSlide()
  }

  setHoveredIndustry(index: number): void { 
    this.hoveredIndustryIndex = index; 
  }
  
  clearHoveredIndustry(): void { 
    this.hoveredIndustryIndex = null; 
  }
  
  isIndustry(item: TemplateItem): item is Industry { 
    return (item as Industry).route !== undefined; 
  }
  
  isPlaceholder(item: TemplateItem): item is { placeholder: true } { 
    return 'placeholder' in item; 
  }

  // --- SERVICES SLIDER (FIXED) ---
  startAutoServiceSlide(): void {
    // Don't start if already running or if section not initialized
    if (this.serviceSlideInterval || !this.servicesInitialized || !this.servicesVisible) return;

    this.serviceSlideInterval = setInterval(() => {
      this.selectServiceSlide(
        (this.activeServiceSlideIndex + 1) % this.paginatedServices.length
      );
    }, 5000);
  }

  clearAutoServiceSlide(): void {
    if (this.serviceSlideInterval) {
      clearInterval(this.serviceSlideInterval);
      this.serviceSlideInterval = null;
    }
  }

  selectServiceSlide(index: number): void {
    this.hoveredServiceIndex = null;
    this.activeServiceSlideIndex = index;
    const currentPage = this.paginatedServices[index];

    if (currentPage) {
      const newTemplateItems: ServiceTemplateItem[] = [...currentPage];
      const placeholdersNeeded = 3 - currentPage.length;
      if (placeholdersNeeded > 0) {
        for (let i = 0; i < placeholdersNeeded; i++) {
          newTemplateItems.push({ placeholder: true });
        }
      }
      this.templateServiceItems = newTemplateItems;
    }

    // DON'T restart the interval here - it creates conflicts
    // The interval is already running from startAutoServiceSlide()
  }

  setHoveredService(index: number): void { 
    this.hoveredServiceIndex = index; 
  }
  
  clearHoveredService(): void { 
    this.hoveredServiceIndex = null; 
  }
  
  isService(item: ServiceTemplateItem): item is Service { 
    return (item as Service).route !== undefined; 
  }
  
  isServicePlaceholder(item: ServiceTemplateItem): item is { placeholder: true } { 
    return 'placeholder' in item; 
  }

scrollToSection(sectionId: string): void {
  this.loadedSections.add(sectionId);

  if (!isPlatformBrowser(this.platformId)) return;

  setTimeout(() => {
    let element = document.getElementById(sectionId);
    if (!element) {
      element = document.getElementById(sectionId + 'ObserverTarget');
    }

    if (element) {
      const rect = (typeof element.getBoundingClientRect === 'function' ? (typeof element.getBoundingClientRect === 'function' ? (typeof element.getBoundingClientRect === 'function' ? element.getBoundingClientRect() : {top:0,left:0,bottom:0,right:0,width:0,height:0,x:0,y:0}) : {top:0,left:0,bottom:0,right:0,width:0,height:0,x:0,y:0}) : {top:0,left:0,bottom:0,right:0,width:0,height:0,x:0,y:0});

      let offset =
        rect.top +
        window.pageYOffset -
        (window.innerHeight / 2) +
        (rect.height / 2);

      // Prevent overscrolling beyond page bottom
      const maxScroll =
        document.documentElement.scrollHeight - window.innerHeight;

      offset = Math.min(offset, maxScroll);

      window.scrollTo({
        top: offset,
        behavior: 'smooth'
      });

      this.activeSection = sectionId;
    }
  }, 50);
}

  // ================= INDUSTRIES HOVER CONTROL =================
  pauseIndustrySlider(): void {
    this.clearAutoSlide();
  }

  resumeIndustrySlider(): void {
    if (this.industriesVisible && !this.slideInterval) {
      this.startAutoSlide();
    }
  }

  // ================= SERVICES HOVER CONTROL =================
  pauseServiceSlider(): void {
    this.clearAutoServiceSlide();
  }

  resumeServiceSlider(): void {
    if (this.servicesInitialized && this.servicesVisible && !this.serviceSlideInterval) {
      this.startAutoServiceSlide();
    }
  }

  openContactModal() {
    this.contactModal.open();
  }

  goToCaseStudies(): void {
    this.router.navigate(['/kalpita-case-studies/case-study']);
  }

  ExploreAboutUs(): void {
    this.router.navigate(['/aboutcompany/about-kalpita-technologies']);
  }
}